<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import { normalizeRegexPattern } from 'shared/helpers/Validators';
import { ATTRIBUTE_MODELS, ATTRIBUTE_TYPES } from './constants';

import NextButton from 'dashboard/components-next/button/Button.vue';
import AttributeOptionsInput from './AttributeOptionsInput.vue';

export default {
  components: {
    NextButton,
    AttributeOptionsInput,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    // Passes 0 or 1 based on the selected AttributeModel tab selected in the UI
    // Needs a better data type, todo: refactor this component later
    selectedAttributeModelTab: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      placeholder: '',
      // Using the prop as default. There is no side effect here as the component
      // is destroyed completely when the modal is closed. The prop doesn't change
      // dynamically when the modal is active.
      attributeModel: this.selectedAttributeModelTab || 0,
      attributeType: 0,
      attributeKey: '',
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      filterable: false,
      values: [],
      show: true,
      tagInputTouched: false,
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
    }),
    models() {
      return ATTRIBUTE_MODELS.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_MODELS.${item.key}`),
      }));
    },
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    isTagInputEmpty() {
      return (
        this.hasOptions &&
        (this.values.length === 0 || this.values.some(value => !value?.trim()))
      );
    },
    isTagInputInvalid() {
      return this.tagInputTouched && this.isTagInputEmpty;
    },
    attributeListValues() {
      return this.values.map(value => value.trim()).filter(Boolean);
    },
    isButtonDisabled() {
      return (
        this.v$.displayName.$invalid ||
        this.uiFlags.isCreating ||
        this.isTagInputEmpty
      );
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeMultipleChoice() {
      return this.attributeType === 8;
    },
    hasOptions() {
      return this.isAttributeTypeList || this.isAttributeTypeMultipleChoice;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
    // Só campos de Negociação podem virar filtro do Dashboard Comercial
    isDealAttribute() {
      return this.attributeModel === 3;
    },
  },

  validations: {
    displayName: { required, minLength: minLength(1) },
    attributeModel: { required },
    attributeType: { required },
  },

  watch: {
    attributeType() {
      this.tagInputTouched = false;
      this.values = [];
    },
  },

  methods: {
    onDisplayNameChange() {
      this.attributeKey = convertToAttributeSlug(this.displayName);
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
    async addAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          placeholder: this.placeholder,
          attribute_model: this.attributeModel,
          attribute_display_type: this.attributeType,
          attribute_key: this.attributeKey,
          attribute_values: this.attributeListValues,
          regex_pattern: normalizeRegexPattern(this.regexPattern),
          regex_cue: this.regexCue,
          filterable: this.isDealAttribute && this.filterable,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="$t('ATTRIBUTES_MGMT.ADD.TITLE')" />

      <form class="flex w-full" @submit.prevent="addAttributes">
        <div class="w-full">
          <label :class="{ error: v$.attributeModel.$error }">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
            <select v-model="attributeModel">
              <option v-for="model in models" :key="model.id" :value="model.id">
                {{ model.option }}
              </option>
            </select>
            <span v-if="v$.attributeModel.$error" class="message">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.ERROR') }}
            </span>
          </label>
          <woot-input
            v-model="displayName"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
            type="text"
            :class="{ error: v$.displayName.$error }"
            :error="
              v$.displayName.$error
                ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
                : ''
            "
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @update:model-value="onDisplayNameChange"
            @blur="v$.displayName.$touch"
          />
          <woot-input
            v-model="placeholder"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.PLACEHOLDER_FIELD.LABEL')"
            type="text"
            :placeholder="
              $t('ATTRIBUTES_MGMT.ADD.FORM.PLACEHOLDER_FIELD.PLACEHOLDER')
            "
          />
          <label :class="{ error: v$.attributeType.$error }">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
            <select v-model="attributeType">
              <option v-for="type in types" :key="type.id" :value="type.id">
                {{ type.option }}
              </option>
            </select>
            <span v-if="v$.attributeType.$error" class="message">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
            </span>
          </label>
          <div v-if="hasOptions" class="mb-4">
            <label class="mb-1 block">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.LABEL') }}
            </label>
            <AttributeOptionsInput
              v-model="values"
              @update:model-value="tagInputTouched = true"
            />
            <label
              v-show="isTagInputInvalid"
              class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
            </label>
          </div>
          <div v-if="isAttributeTypeText">
            <input
              v-model="regexEnabled"
              type="checkbox"
              @input="toggleRegexEnabled"
            />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
          </div>
          <woot-input
            v-if="isAttributeTypeText && isRegexEnabled"
            v-model="regexPattern"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
            type="text"
            :placeholder="
              $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
            "
          />
          <woot-input
            v-if="isAttributeTypeText && isRegexEnabled"
            v-model="regexCue"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
          />
          <div v-if="isDealAttribute" class="mb-4">
            <input v-model="filterable" type="checkbox" />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_FILTER.LABEL') }}
          </div>
          <div
            class="flex flex-row items-center justify-between w-full gap-2 px-0 py-2"
          >
            <span class="key-value">{{ attributeKey }}</span>
            <div class="flex flex-row gap-2">
              <NextButton
                faded
                slate
                type="reset"
                :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
                @click.prevent="onClose"
              />
              <NextButton
                type="submit"
                :label="$t('ATTRIBUTES_MGMT.ADD.SUBMIT')"
                :disabled="isButtonDisabled"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style lang="scss" scoped>
.key-value {
  @apply text-xs text-n-slate-10 font-mono;
}
</style>
